// AnimalAddendumTerms — what the household form says about money, under the animal cards.
//
// The server's own words (its `terms` displays), so this page, the emails and the addendum
// are never a dollar apart. Four shapes, and only the amounts differ between a priced round
// and the rest:
//   • a priced round                 → "Animal rent is $50.00 per month per animal …"
//   • nothing is priced ($0 and $0)  → "There's no additional animal rent or deposit for this animal. …"
//   • a round that lists covered animals up front (RRA v2.2 part B): one line per covered
//     animal, "{Name} — no additional animal rent or deposit.", and then the rates are for
//     "any other animal you add".
//   • a covered animal whose charge is still open (WEL-182, staff marked it pending): its own
//     line instead — "{Name} — $0 for now. If we don't waive it, …" (pendingAnimalLine).
// Nothing here ever says why an animal carries no additional charge (counsel Rule H).
//
// Its own small file so the wording can be rendered and tested without the page's router,
// fetches and photo uploads.

import type { CSSProperties, JSX } from 'react';
import { pendingAnimalLine, type AnimalAddendumCoveredAnimal } from '@/lib/animalAddendumForm';

export interface AnimalAddendumTermsProps {
  terms: {
    rentPerNewAnimalDisplay: string;
    depositPerNewAnimalDisplay: string;
    rentStartDisplay: string;
  };
  /** Animals the form lists up front at no additional charge (the server view's `coveredAnimals`). */
  covered?: ReadonlyArray<{
    id: string;
    name?: string;
    /** WEL-182: set when the charge is still open — the line states what applies if it stays that way. */
    pendingDecision?: AnimalAddendumCoveredAnimal['pendingDecision'];
  }>;
}

const WRAP: CSSProperties = {
  margin: '0 0 var(--space-5)',
  color: 'var(--fg-muted)',
  fontSize: 'var(--text-sm)',
};

export function AnimalAddendumTerms({ terms, covered = [] }: AnimalAddendumTermsProps): JSX.Element | null {
  if (!terms.rentPerNewAnimalDisplay) return null;
  const nothingPriced =
    terms.rentPerNewAnimalDisplay === '$0.00' && terms.depositPerNewAnimalDisplay === '$0.00';
  const anyPending = covered.some((c) => c.pendingDecision !== undefined);
  return (
    <div style={WRAP}>
      {!nothingPriced || anyPending
        ? covered.map((c) => (
            <p key={c.id} style={{ margin: '0 0 var(--space-2)' }}>
              {c.pendingDecision
                ? pendingAnimalLine(c.name, c.pendingDecision)
                : `${c.name || 'This animal'} — no additional animal rent or deposit.`}
            </p>
          ))
        : null}
      <p style={{ margin: 0 }}>
        {nothingPriced ? (
          anyPending ? (
            <>
              Welcome Home Properties will send the addendum for everyone to sign once this is
              submitted.
            </>
          ) : (
            <>
              There's no additional animal rent or deposit for this animal. Welcome Home Properties
              will send the addendum for everyone to sign once this is submitted.
            </>
          )
        ) : covered.length > 0 ? (
          <>
            Any other animal you add is <strong>{terms.rentPerNewAnimalDisplay} per month</strong>{' '}
            in animal rent and a <strong>{terms.depositPerNewAnimalDisplay} deposit</strong> per
            animal. Welcome Home Properties will send the addendum for everyone to sign once this
            is submitted.
          </>
        ) : (
          <>
            Animal rent is <strong>{terms.rentPerNewAnimalDisplay} per month per animal</strong>
            {terms.rentStartDisplay ? <> starting {terms.rentStartDisplay}</> : null}, and a{' '}
            <strong>{terms.depositPerNewAnimalDisplay} deposit per animal</strong> is due when the
            addendum is signed. Welcome Home Properties will send it to everyone to sign as soon as
            this is submitted.
          </>
        )}
      </p>
    </div>
  );
}
